import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import type { Patient } from '@medplum/fhirtypes'
import { ActionIcon, Badge, Loader, Menu, Select, Tabs, Text, TextInput } from '@mantine/core'
import {
  IconDotsVertical,
  IconExternalLink,
  IconLayoutSidebarRight,
  IconMessageCircle,
  IconSearch,
} from '@tabler/icons-react'
import { BOARD_STATUS_VALUES } from '@behuman/core-contract'
import { useBoardMutations } from '@/pages/Boards/hooks'
import { ItemDrawer } from '@/pages/Boards/ItemDrawer'
import { latestNoteTime } from '@/pages/CareCoordination/queueFilters'
import { dueSeverity, MONO, QUEUE_COLOR, STATUS_META } from '@/pages/CareCoordination/queueTheme'
import type { ChartItem } from '../PatientChart.utils.tsx'
import { buildPatientChartRelativePath, parsePatientChartSplat } from '../../patientChartPath'
import { usePatientCareTasks, type PatientCareTask } from './usePatientCareTasks'
import {
  TASK_TYPE_LABELS,
  countByTab,
  filterCareTasks,
  type CareTaskFilters,
  type CareTaskTab,
} from './careTaskFilters'

const SECTION = 'care-coordination-tasks'

type Props = {
  patient: Patient
  item: ChartItem
  refetchPatient: () => void
}

/**
 * Patient-level history of the Care Queue (PD-1870): every task for this patient,
 * open and completed, across the org's boards. The board stays the workspace;
 * this is the record. Clicking a task opens the board's own drawer.
 */
export function CareCoordinationTasks({ patient }: Props) {
  const params = useParams()
  const navigate = useNavigate()
  const { data, isLoading, isError } = usePatientCareTasks(patient.id)
  const tasks = useMemo(() => data?.tasks ?? [], [data])

  const [filters, setFilters] = useState<CareTaskFilters>({
    tab: 'all',
    search: '',
    type: null,
    status: null,
    order: 'newest',
  })
  const [selectedId, setSelectedId] = useState<string | undefined>()

  // Deep link: /patients/<patientId>/care-coordination-tasks/<itemId>.
  useEffect(() => {
    setSelectedId(parsePatientChartSplat(params['*']).resourceId)
  }, [params])

  // Relative paths: the chart is also hosted under /call/:encounterId/*.
  const select = (itemId: string | undefined) => {
    setSelectedId(itemId)
    void navigate(buildPatientChartRelativePath(SECTION, itemId))
  }

  const counts = countByTab(tasks)
  const shown = filterCareTasks(tasks, filters)
  // Read live from the query so drawer edits (comments, checklist) re-render it.
  const selected = tasks.find((t) => t.item.id === selectedId)

  // Reading a task here reads it on the board too, as opening it in the Care Queue
  // does: on open, and again when a newer note lands while the drawer stays open
  // (e.g. the viewer's own reply). The ref keeps it to one call per state.
  const { markRead } = useBoardMutations(selected?.item.boardId)
  const markedRef = useRef('')
  useEffect(() => {
    if (!selected) return
    const latest = latestNoteTime(selected.item)
    if (!latest) return
    const stateKey = `${selected.item.id} ${latest}`
    if (markedRef.current === stateKey) return
    markedRef.current = stateKey
    void markRead.mutateAsync({ itemId: selected.item.id, seenAt: latest }).catch(() => undefined)
  }, [selected, markRead])

  const set = <K extends keyof CareTaskFilters>(key: K, value: CareTaskFilters[K]) =>
    setFilters((f) => ({ ...f, [key]: value }))

  return (
    <div className="flex w-full flex-col gap-4 p-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Care Coordination Tasks</h2>
        <Text size="sm" c="dimmed">
          All care coordination tasks for this patient, including current and completed tasks, in
          chronological order.
        </Text>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <Tabs value={filters.tab} onChange={(tab) => set('tab', (tab as CareTaskTab) ?? 'all')}>
          <Tabs.List>
            <TabWithCount value="all" label="All tasks" count={counts.all} />
            <TabWithCount value="open" label="Open" count={counts.open} />
            <TabWithCount value="completed" label="Completed" count={counts.completed} />
          </Tabs.List>
        </Tabs>

        <div className="flex flex-wrap items-center gap-2">
          <TextInput
            aria-label="Search tasks"
            placeholder="Search tasks..."
            leftSection={<IconSearch size={14} />}
            value={filters.search}
            onChange={(e) => set('search', e.currentTarget.value)}
            className="w-56"
          />
          <Select
            aria-label="Type"
            placeholder="All types"
            clearable
            data={Object.entries(TASK_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
            value={filters.type}
            onChange={(value) => set('type', value as CareTaskFilters['type'])}
            className="w-40"
          />
          <Select
            aria-label="Status"
            placeholder="All statuses"
            clearable
            data={[...BOARD_STATUS_VALUES]}
            value={filters.status}
            onChange={(value) => set('status', value)}
            className="w-40"
          />
          <Select
            aria-label="Sort"
            allowDeselect={false}
            data={[
              { value: 'newest', label: 'Newest first' },
              { value: 'oldest', label: 'Oldest first' },
            ]}
            value={filters.order}
            onChange={(value) => set('order', value === 'oldest' ? 'oldest' : 'newest')}
            className="w-36"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <Loader size="sm" />
        </div>
      ) : isError ? (
        <EmptyState text="Couldn't load care coordination tasks." />
      ) : tasks.length === 0 ? (
        <EmptyState text="No care coordination tasks for this patient yet." />
      ) : shown.length === 0 ? (
        <EmptyState text="No tasks match these filters." />
      ) : (
        <TaskTimeline tasks={shown} onSelect={(task) => select(task.item.id)} />
      )}

      <ItemDrawer item={selected?.item ?? null} onClose={() => select(undefined)} />
    </div>
  )
}

function TabWithCount({ value, label, count }: { value: string; label: string; count: number }) {
  return (
    <Tabs.Tab
      value={value}
      rightSection={
        <Badge size="sm" variant="light" color="gray" circle={count < 10}>
          {count}
        </Badge>
      }
    >
      {label}
    </Tabs.Tab>
  )
}

function EmptyState({ text }: { text: string }) {
  return <div className="flex h-48 items-center justify-center text-sm text-gray-400">{text}</div>
}

/** Tasks grouped under the day they were created, in the order given. */
function TaskTimeline({
  tasks,
  onSelect,
}: {
  tasks: PatientCareTask[]
  onSelect: (task: PatientCareTask) => void
}) {
  const days: { day: string; tasks: PatientCareTask[] }[] = []
  for (const task of tasks) {
    const day = dayjs(task.item.createdAt).format('YYYY-MM-DD')
    const last = days.at(-1)
    if (last?.day === day) last.tasks.push(task)
    else days.push({ day, tasks: [task] })
  }
  const today = dayjs().format('YYYY-MM-DD')

  return (
    <div className="flex flex-col gap-5">
      {days.map(({ day, tasks: dayTasks }) => (
        <section key={day} className="flex flex-col gap-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
            <span
              className="size-2 rounded-full"
              style={{
                background: dayTasks.some((t) => t.isOpen) ? QUEUE_COLOR.wait : QUEUE_COLOR.done,
              }}
            />
            {dayjs(day).format('MMM D, YYYY')}
            {day === today && <span className="font-normal text-gray-500">(Today)</span>}
          </div>
          {dayTasks.map((task) => (
            <TaskRow key={task.item.id} task={task} onSelect={() => onSelect(task)} />
          ))}
        </section>
      ))}
    </div>
  )
}

function TaskRow({ task, onSelect }: { task: PatientCareTask; onSelect: () => void }) {
  const navigate = useNavigate()
  const severity = dueSeverity(task.dueDate, !task.isOpen)
  const status = task.status ? STATUS_META[task.status] : undefined
  // No status on the board reads "—" there, so here too (PD-1879) — never "Not started".
  const statusLabel = task.status ?? '—'

  return (
    <div
      data-testid="care-task-row"
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect()
        }
      }}
      // The name flexes beside a fixed-width group of fields, so every value shows in
      // full and the columns line up across cards (PD-1880). Without room for both,
      // the fields drop below the name instead of squeezing it.
      className="relative flex cursor-pointer flex-wrap items-center gap-x-6 gap-y-3 rounded-md border border-l-4 border-gray-200 bg-white py-3 pr-12 pl-4 transition hover:border-gray-300 hover:shadow-sm"
      style={{ borderLeftColor: task.isOpen ? severity.bar : QUEUE_COLOR.done }}
    >
      <div className="min-w-64 flex-1 basis-64">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <h3 className="text-sm font-semibold break-words text-gray-900">{task.item.name}</h3>
          <Badge size="xs" variant="light" color="blue" className="shrink-0">
            {TASK_TYPE_LABELS[task.type]}
          </Badge>
        </div>
        <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
          <span className="break-words">{task.boardName}</span>
          {task.commentCount > 0 && (
            <span className="flex shrink-0 items-center gap-1 text-[#2f6bd8]">
              <IconMessageCircle size={13} />
              {task.commentCount} comment{task.commentCount === 1 ? '' : 's'}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-2">
        <Field label="Created" width="w-40">
          {formatTaskDateTime(task.item.createdAt)}
        </Field>

        <Field label="Assigned to" width="w-36">
          {task.assignee?.display ?? '—'}
        </Field>

        <Field label="Due date" width="w-40">
          <span className="flex flex-wrap items-center gap-1.5">
            <span style={{ fontFamily: MONO }}>
              {task.dueDate ? dayjs(task.dueDate).format('MMM D, YYYY') : '—'}
            </span>
            {task.isOpen && severity.chip && (
              <span
                className="rounded px-1.5 py-0.5 text-[11px]"
                style={{ background: severity.chip.bg, color: severity.chip.color }}
              >
                {severity.chip.label}
              </span>
            )}
          </span>
        </Field>

        <Field label="Status" width="w-28">
          <span
            className="flex items-center gap-1.5"
            style={{ color: status?.color ?? QUEUE_COLOR.muted, fontWeight: status?.weight ?? 400 }}
          >
            {status?.dot && (
              <span className="size-2 rounded-full" style={{ background: status.dot }} />
            )}
            {statusLabel}
          </span>
        </Field>

        {task.isOpen ? (
          <Field label="Progress" width="w-44">
            {task.checklist
              ? `${task.checklist.done} of ${task.checklist.total} ${task.checklist.total === 1 ? 'step' : 'steps'}`
              : '—'}
          </Field>
        ) : (
          <Field label="Completed" width="w-44">
            {task.completedAt ? (
              <>
                <div>{formatTaskDateTime(task.completedAt)}</div>
                {task.completedBy && (
                  <div className="text-xs text-gray-500">by {task.completedBy.display}</div>
                )}
              </>
            ) : (
              // Completed before completion was recorded (PD-1870): no date to show.
              '—'
            )}
          </Field>
        )}
      </div>

      {/* Pinned to the card's right edge, outside the wrapping flow, in the space every
          card reserves with its right padding — so it never wraps onto a line of its own
          and the columns line up whether a card has a menu or not. */}
      {task.isOpen && (
        <div className="absolute top-1/2 right-3 -translate-y-1/2">
          <TaskActions onOpen={onSelect} onOpenInQueue={() => openInQueue(task)} />
        </div>
      )}
    </div>
  )

  // The board's own deep link (see LinkedTasksPanel): opens the task on its board.
  function openInQueue(t: PatientCareTask) {
    void navigate(`/appointments?tab=care-queue&board=${t.item.boardId}&item=${t.item.id}`)
  }
}

/**
 * The open card's actions menu (PD-1884). Navigation only: editing a task stays on the
 * Care Queue board. Clicks and keys stop here so the card underneath doesn't also open.
 */
function TaskActions({ onOpen, onOpenInQueue }: { onOpen: () => void; onOpenInQueue: () => void }) {
  return (
    <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon variant="subtle" color="gray" aria-label="Task actions">
            <IconDotsVertical size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item leftSection={<IconLayoutSidebarRight size={14} />} onClick={onOpen}>
            Open task
          </Menu.Item>
          <Menu.Item leftSection={<IconExternalLink size={14} />} onClick={onOpenInQueue}>
            Open in Care Queue
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </div>
  )
}

// "Oct 5, 2026 09:12 AM" — the same date style as Due date, plus the time (PD-1881).
// Local time, like the day headers the rows are grouped under.
function formatTaskDateTime(iso: string): string {
  return dayjs(iso).format('MMM D, YYYY hh:mm A')
}

function Field({
  label,
  width,
  children,
}: {
  label: string
  /** Fixed, so each column lines up across the cards. */
  width: string
  children: React.ReactNode
}) {
  return (
    <div className={`${width} shrink-0 text-sm text-gray-800`}>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="break-words">{children}</div>
    </div>
  )
}
